import { OperatorValue, QueryField, QueryRule } from '../../../../../types/QueryBuilder';
import { isRuleValid } from '../../../../../utils/queryBuilderUtils';
import { COUNTER_TYPE_FIELD_NAME, resolveFieldLabel, resolveFieldValues, Translate } from './fieldConfig';

export type { Translate };

export interface SummaryField {
    id: string;
    label?: string;
    intro?: string;
    values: string[];
    secondIntro?: string;
    secondValues?: string[];
}

const OPERATOR_LABEL_KEY: Partial<Record<OperatorValue, string>> = {
    '!=': 'notEquals',
    '>': 'greaterThan',
    '<': 'lessThan',
    '>=': 'greaterOrEqual',
    '<=': 'lessOrEqual',
    between: 'between',
    in: 'in',
    'not in': 'notIn',
    contains: 'contains',
    'not contains': 'notContains',
};

// Les valeurs brutes du champ (ex: SOFT1/SOFT2) sont fusionnées en une seule entrée "Softbounce"
// pour l'éditeur (voir resolveFieldValues) : on applique la même fusion ici pour que le résumé
// affiche le même libellé, quelle que soit la valeur brute sélectionnée.
const resolveValueLabel = (field: QueryField, value: unknown, t: Translate): string => {
    const strValue = String(value);
    const values = resolveFieldValues(field, t) ?? field.values;
    const match = values?.find(fv => fv.name === strValue || fv.mergedNames?.includes(strValue));
    return match?.label ?? strValue;
};

const toValueList = (value: QueryRule['value']): string[] => {
    if (Array.isArray(value)) return value.map(String);
    if (value === null || value === undefined || value === '') return [];
    return [String(value)];
};

const toUniqueLabels = (field: QueryField, values: string[], t: Translate): string[] =>
    Array.from(new Set(values.map(v => resolveValueLabel(field, v, t))));

const parseBetween = (rule: QueryRule): [string, string] => {
    const raw = String(rule.value ?? '');
    if (raw.includes('|')) {
        const [min, max] = raw.split('|');
        return [min || '', max || ''];
    }
    return [raw, String(rule.valueTo ?? '')];
};

const formatDateValue = (raw: string, formatLocale: string): string => {
    if (!raw) return '?';
    const date = new Date(raw);
    return Number.isNaN(date.getTime()) ? raw : date.toLocaleDateString(formatLocale);
};

const resolveFieldValueLabel = (fields: QueryField[], rules: QueryRule[], fieldName: string, t: Translate): string => {
    const field = fields.find(f => f.name === fieldName);
    const rule = rules.find(r => r.field === fieldName);
    if (!field || !rule || !isRuleValid(rule)) return '';
    return toUniqueLabels(field, toValueList(rule.value), t).join(', ');
};

const inNotInIntro = (t: Translate, rule: QueryRule, notInKey: string, inKey: string): string =>
    t(rule.operator === 'not in' ? notInKey : inKey);

// Accord réel du nom au singulier/pluriel selon le nombre d'éléments sélectionnés (ex: "au magasin"
// vs "aux magasins") — à utiliser partout où le sujet de la phrase varie avec la liste, contrairement
// à des tournures comme "Les clients appartiennent à :" qui restent invariables quel que soit le compte.
const pluralizeUnit = (count: number, singular: string, plural: string): string => (count > 1 ? plural : singular);

function genericSummaryField(field: QueryField, rule: QueryRule, t: Translate): SummaryField {
    const operatorKey = rule.operator ? OPERATOR_LABEL_KEY[rule.operator] : undefined;
    const operatorLabel = operatorKey ? `${t(`pages.clients.queryBuilder.operators.${operatorKey}`)} ` : '';

    let value: string;
    if (rule.operator === 'between') {
        const [min, max] = parseBetween(rule);
        value = t('pages.clients.queryBuilder.simple.summary.betweenValue', { min: min || '?', max: max || '?' });
    } else if (Array.isArray(rule.value)) {
        value = toUniqueLabels(field, rule.value.map(String), t).join(', ');
    } else if (field.valueEditorType === 'checkbox' && !field.values?.length) {
        value = t('pages.clients.queryBuilder.simple.summary.yes');
    } else {
        value = resolveValueLabel(field, rule.value, t);
    }

    return { id: rule.id, values: [t('pages.clients.queryBuilder.simple.summary.item', { field: resolveFieldLabel(field, t), operator: operatorLabel, value })] };
}

const sentenceField = (rule: QueryRule, sentence: string): SummaryField => ({ id: rule.id, values: [sentence] });

const optinSummaryField = (rule: QueryRule, t: Translate, acceptKey: string, refuseKey: string): SummaryField =>
    sentenceField(rule, t(String(rule.value) === '0' ? refuseKey : acceptKey));

function listField(field: QueryField, rule: QueryRule, t: Translate, intro: string, withLabel = true): SummaryField {
    return {
        id: rule.id,
        label: withLabel ? resolveFieldLabel(field, t) : undefined,
        intro,
        values: toUniqueLabels(field, toValueList(rule.value), t),
    };
}

function dualListField(field: QueryField, rule: QueryRule, t: Translate, belongsKey: string, doesNotBelongKey: string): SummaryField {
    const includeValues = toUniqueLabels(field, toValueList(rule.value), t);
    const excludeValues = toUniqueLabels(field, (rule.notValue ?? []).map(String), t);
    return {
        id: rule.id,
        label: resolveFieldLabel(field, t),
        intro: includeValues.length > 0 ? t(belongsKey) : undefined,
        values: includeValues,
        secondIntro: excludeValues.length > 0 ? t(doesNotBelongKey) : undefined,
        secondValues: excludeValues,
    };
}

type SummaryBuilder = (field: QueryField, rule: QueryRule, t: Translate, formatLocale: string, fields: QueryField[], rules: QueryRule[]) => SummaryField | null;

const FIELD_SUMMARY_BUILDERS: Record<string, SummaryBuilder> = {
    PERSON_ENTITY: (field, rule, t) => {
        const count = toValueList(rule.value).length;
        const unit = pluralizeUnit(count, t('pages.clients.queryBuilder.simple.units.store'), t('pages.clients.queryBuilder.simple.units.stores'));
        return listField(
            field, rule, t,
            rule.operator === 'not in'
                ? t('pages.clients.queryBuilder.simple.summary.storesNotBelong', { unit })
                : t('pages.clients.queryBuilder.simple.summary.storesBelong', { unit }),
            false
        );
    },
    CUSTOMER_IDENTIFIER: (field, rule, t) => dualListField(
        field, rule, t,
        'pages.clients.queryBuilder.simple.summary.belongsToClients',
        'pages.clients.queryBuilder.simple.summary.doesNotBelongToClients'
    ),
    CUSTOMER_LIST_BY_IDENTIFIER: (field, rule, t) => dualListField(
        field, rule, t,
        'pages.clients.queryBuilder.simple.summary.belongsToClients',
        'pages.clients.queryBuilder.simple.summary.doesNotBelongToClients'
    ),
    CUSTOMER_LIST_BY_CARTE: (field, rule, t) => dualListField(
        field, rule, t,
        'pages.clients.queryBuilder.simple.summary.belongsToClients',
        'pages.clients.queryBuilder.simple.summary.doesNotBelongToClients'
    ),
    CUSTOMER_SUBSCRIPTIONS_TYPE: (field, rule, t) => listField(
        field, rule, t,
        t(rule.operator === 'not in'
            ? 'pages.clients.queryBuilder.simple.summary.doesNotBelongToCategory'
            : 'pages.clients.queryBuilder.simple.summary.belongsToCategory')
    ),
    CUSTOMER_CITY: (field, rule, t) => listField(
        field, rule, t,
        inNotInIntro(t, rule, 'pages.clients.queryBuilder.simple.summary.doesNotLiveIn', 'pages.clients.queryBuilder.simple.summary.livesIn')
    ),
    CUSTOMER_DEPARTMENT: (field, rule, t) => listField(
        field, rule, t,
        inNotInIntro(t, rule, 'pages.clients.queryBuilder.simple.summary.doesNotLiveIn', 'pages.clients.queryBuilder.simple.summary.livesIn')
    ),
    CUSTOMER_COUNTRY: (field, rule, t) => listField(
        field, rule, t,
        inNotInIntro(t, rule, 'pages.clients.queryBuilder.simple.summary.doesNotLiveIn', 'pages.clients.queryBuilder.simple.summary.livesIn')
    ),
    CUSTOMER_ACCOUNT: (_field, rule, t, _formatLocale, fields, rules) => {
        if (rule.operator !== 'between') return null;
        const [min, max] = parseBetween(rule);
        if (!min && !max) return null;
        const accountType = resolveFieldValueLabel(fields, rules, COUNTER_TYPE_FIELD_NAME, t) || t('pages.clients.queryBuilder.simple.summary.genericCounterType');
        const key = min && max
            ? 'pages.clients.queryBuilder.simple.summary.scorePointsBetween'
            : min
                ? 'pages.clients.queryBuilder.simple.summary.scorePointsMin'
                : 'pages.clients.queryBuilder.simple.summary.scorePointsMax';
        return sentenceField(rule, t(key, { accountType, min, max }));
    },
    CUSTOMER_SCORE: (field, rule, t) => listField(field, rule, t, ''),
    CUSTOMER_ACTIVATION_DATE: (_field, rule, t, formatLocale) => {
        if (rule.operator !== 'between') return null;
        const [min, max] = parseBetween(rule);
        return sentenceField(rule, t('pages.clients.queryBuilder.simple.summary.cardOpenedBetween', {
            min: formatDateValue(min, formatLocale), max: formatDateValue(max, formatLocale),
        }));
    },
    CUSTOMER_SCORE_BEST: (_field, rule, t) => {
        if (rule.operator !== '<') return null;
        return sentenceField(rule, t('pages.clients.queryBuilder.simple.summary.topClients', { value: String(rule.value ?? '') }));
    },
    TRANSACTION_PERIOD: (_field, rule, t, formatLocale) => {
        if (rule.operator !== 'between') return null;
        const [min, max] = parseBetween(rule);
        return sentenceField(rule, t('pages.clients.queryBuilder.simple.summary.purchasePeriodBetween', {
            min: formatDateValue(min, formatLocale), max: formatDateValue(max, formatLocale),
        }));
    },
    TRANSACTION_NB: (_field, rule, t) => {
        if (rule.operator !== '>' && rule.operator !== '>=') return null;
        const value = Number(rule.value ?? 0);
        const unit = pluralizeUnit(value, t('pages.clients.queryBuilder.simple.units.purchase'), t('pages.clients.queryBuilder.simple.units.purchases'));
        return sentenceField(rule, t('pages.clients.queryBuilder.simple.summary.minPurchases', { value: String(rule.value ?? ''), unit }));
    },
    CUSTOMER_OPTIN_EMAIL: (_field, rule, t) => optinSummaryField(
        rule, t, 'pages.clients.queryBuilder.simple.summary.optinEmail', 'pages.clients.queryBuilder.simple.summary.optinEmailRefuse'
    ),
    CUSTOMER_OPTIN_SMS: (_field, rule, t) => optinSummaryField(
        rule, t, 'pages.clients.queryBuilder.simple.summary.optinSms', 'pages.clients.queryBuilder.simple.summary.optinSmsRefuse'
    ),
    CUSTOMER_OPTIN_COURRIER: (_field, rule, t) => optinSummaryField(
        rule, t, 'pages.clients.queryBuilder.simple.summary.optinCourrier', 'pages.clients.queryBuilder.simple.summary.optinCourrierRefuse'
    ),
    CUSTOMER_STATUS_EMAIL: (field, rule, t) => listField(field, rule, t, ''),
    CUSTOMER_STATUS_SMS: (field, rule, t) => listField(field, rule, t, ''),
    CUSTOMER_STATUS_COURRIER: (field, rule, t) => listField(field, rule, t, ''),
};

export function buildSummaryField(field: QueryField, rule: QueryRule, t: Translate, formatLocale: string, fields: QueryField[], rules: QueryRule[]): SummaryField {
    const built = FIELD_SUMMARY_BUILDERS[field.name]?.(field, rule, t, formatLocale, fields, rules);
    return built ?? genericSummaryField(field, rule, t);
}

export const HIDDEN_FROM_SUMMARY_FIELD_NAMES = new Set([COUNTER_TYPE_FIELD_NAME]);
